import React, { useState, useEffect, useCallback, useRef } from "react";
import assert from "utils/assert";
import { useToast, Flex, Box, Checkbox, Divider, Button, HStack, Input, Spinner, Textarea, VStack, Text, Mark } from "@chakra-ui/react";
import { useQuery } from "react-query";
import {
  getAIContext,
  postChat,
  apiGetThread,
  postSourcesUpdate,
  get_json_content,
  displayThreadStatus,
  Thread,
  AGENT_CANVAS,
} from "api/ai/ai.service";
import MarkdownText from "components/MarkdownText";
import { showSuccessToast, showErrorToast } from "utils";
import LocalSpinner from "components/LocalSpinner";
import { getFolderForRound } from "api/common/fileinfo";
import { useSelector } from "react-redux";
import { AIFeedbackThumbsUpDown, AIFeedbackComment } from "./AIFeedbackControls";
import fileinfoService, { apiUpload } from "api/common/fileinfo.service";
import { SmallText } from "components/text/TextTypes";
import { multipartAnswerToString } from "pages/ISSB/IssbDataInput/helper";
import {
  ArrowCircleUp,
  CaretDown,
  CaretUp,
  Copy,
  Files,
  MagnifyingGlass,
  Plus,
  Sparkle,
  Chat,
  ThumbsUp,
  ThumbsDown,
  ClockCounterClockwise,
  Info,
  Eye,
  EyeClosed,
  HourglassMedium,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import DevOnly from "../../../../context/Auth/DevOnly";
import AIChatMessage from "./AIChatMessage";
import { decidePollThreadAction, isQueuedTaskResponse } from "./chatQueueRouting";
import LifecycleState from "api/common/LifecycleState";
import * as Sentry from "@sentry/react";
import Wrapper from "utils/Wrapper";
import { useAuth } from "context";
import questionnaireService from "api/issb/questionnaire.service";
import { useFoundationV2Years } from "hooks/query/issb";
import { isModuleAvailable } from "utils/AuthUtils";
import MODULES from "config/modules";
import { chatFoundationYearIds } from "utils/foundationYearChoice";
import reportDocService from "api/issb/reportdoc.service";
import AIProgressWidget from "pages/ISSB/AITasks/AIProgressWidget";
import {
  fileinfoIdEquals,
  normalizeFileInfo,
  normalizeFileInfoList,
  normalizeFileInfoSpec,
  normalizeFileinfoIdList,
  preferIdsFromQuestionFiles,
} from "utils/fileinfoIds";

/**
 * The guts of AIAssistPanel. Chat plus ESG-features:
 *
 *  - load/save thread from our backend
 *  - File upload
 *  - round -> folder -> limit sources with a FileinfoSpec
 *  - (fixed) Buttons
 *
 * the context sent to the AI is managed via setAIContext()
 *
 * @param {*} param0
 * @param {FileInfoSpec?} fileinfoSpec Normally unset, and we make this from the current round
 * @returns
 */
export default function AIChatWidget({
  reportdoc_id,
  threadId,
  setThreadId,
  cannotAddSource,
  fileinfoSpec,
  agent = "chat",
  onChatResponse,
  onThreadUpdate,
  height,
  dontShowEmptyChatDefaultMessage = false,
  initialMessages,
  initialInput,
  initialDisplayInput,
  autoSend = false,
  onToggleHistory,
  contextualPrompts,
}) {
  // Use a wrapper to avoid a stale thread getting baked into the polling function
  const [threadWrapper, setThreadWrapper] = useState(new Wrapper(null));
  const [messages, setMessages] = useState(initialMessages || []);

  const [inputValue, setInputValue] = useState(initialInput || "");
  const [buttons, setButtons] = useState([]);
  const [isLoading, setIsLoading] = useState(); // in-chat spinner
  const [loadingThread, setLoadingThread] = useState(false); // initial thread load spinner
  const [showAllMessages, setShowAllMessages] = useState(false); // for debug: see tool calls etc
  const [slow, setSlow] = useState(false); // slow-mode for AI
  // scroll to the bottom of the messages
  const messagesEndRef = React.useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Listen for external "send message to existing chat" events
  const handleSendRef = React.useRef(null);
  useEffect(() => {
    const handler = (e) => {
      const text = e.detail?.text;
      const displayText = e.detail?.displayText;
      if (text && handleSendRef.current) {
        handleSendRef.current(text, displayText);
      }
    };
    window.addEventListener("ai-chat-send-message", handler);
    return () => window.removeEventListener("ai-chat-send-message", handler);
  }, []);

  // Auto-send initialInput on mount if requested
  const autoSentRef = React.useRef(false);
  useEffect(() => {
    if (autoSend && initialInput && handleSendRef.current && !autoSentRef.current) {
      autoSentRef.current = true;
      handleSendRef.current(initialInput, initialDisplayInput);
    }
  }); // runs every render until handleSendRef.current is set

  // Poll for updates
  // Hello @Ye-Lynn - notes on where polling is:
  // - handleSendMessage should start polling
  // - pollThread is called to start polling, and then polls repeatedly
  // - Thread.state is used to switch polling off.
  // - While polling, the thread.status is displayed so the user can see things are happening
  // TODO:
  // - TODO If handleSendMessage gets a timeout waiting on postChat
  //   - we should continue polling until the thread is done / failed.
  // - TODO pollThread can also call handleChatResponse (e.g. for the case where there was a timeout)
  //    - But be careful to avoid double-processing the same response.
  // - TODO a nice user message if there is an error (other than timeout, which we'll handle with polling)
  // - TODO if you leave a report-writer page while it's waiting for a response, then return
  //    -- use thread.state = running to restart polling when you return.
  // Stretch goal:
  // - TODO if you visit an unready report - show thread.status and updates as they happen (including reloading the reportDoc which onChatResponse does).

  // Use ref vs state to avoid stale closures and prevent unnecessary re-render
  // awaitingQueueCheck: ESG-3952 - set while a fire-and-forget postChat() (the "have
  // threadId" path below) is in flight, cleared once its response confirms whether the
  // request was queued as a report-writer task. pollThread() must not act on the (possibly
  // stale, from a PRIOR exchange) existing thread's state while this is true - a queued
  // request never touches that thread_id at all, so a stale threadData.state === "done"
  // could otherwise make pollThread() fire handleChatResponse with the wrong content before
  // we've had a chance to switch over to pollReportDocTask().
  const pollingState = React.useRef({ polling: false, timeout: null, awaitingQueueCheck: false }); // polling for new messages
  const [threadStatus, setThreadStatus] = useState();
  const [statusTrace, setStatusTrace] = useState([]);
  const [messageSentAt, setMessageSentAt] = useState(null);
  const [latestPolledMessage, setLatestPolledMessage] = useState();
  // activeTaskId: ESG-3952 - set when a large-scope canvas request got routed to a
  // report-writer task (see isQueuedTaskResponse below) instead of a normal synchronous chat
  // response. While set, the loading state renders <AIProgressWidget> (a real, live-updating
  // progress bar reading task.progress via GET /v0/task/{id}) instead of the plain spinner -
  // pollReportDocTask() still owns knowing when the update is actually DONE (the reportdoc's
  // own state, not the task's, is the true completion signal - see its own comment), but no
  // longer has to carry the visual "is anything happening" burden alone for the whole
  // multi-section duration.
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [isStale, setIsStale] = useState(false);

  /**
   * Handle a chat response by extracting the AI message, processing content,
   * updating messages state, and calling callbacks.
   * Can be called from handleSendMessage or pollThread.
   * Note: Why not fix on just one of handleSendMessage or pollThread?
   *  - handleSendMessage can be faster (by 1 second)
   *  - pollThread can be more reliable (e.g. if the connection is lost)
   * @param {ChatResponse|null} response - The chat response (null if error occurred)
   * @param {Error|null} error - The error if one occurred
   * @param {ChatMessage[]} messagesWithUserMessage - Messages array with user message already added
   * @param {Function} setMessages - State setter for messages
   * @param {Function} scrollToBottom - Function to scroll to bottom
   * @param {Function} onChatResponse - Optional callback for chat response
   *
   *
   * TODO: to clean up - do we need both response and thread here? consider separating the data parsing and error handling.
   */
  const handleChatResponse = useCallback(
    (
    response,
    thread,
    error,
    messagesWithUserMessage,
    setMessages,
    scrollToBottom,
    onChatResponse
  ) => {
    if (!isLoading) {
      console.error("handleChatResponse called while not loading! :(");
    } else {
      console.log("handleChatResponse called while loading :)", isLoading);
    }
    const threadState = thread?.state || "error"; // Note: no thread?.state would mean something went wrong
    setIsLoading(false);
    // Stop polling only if thread state is done or failed (not if still running)
    if (threadState === "done" || threadState === "failed") {
      pollingState.current.polling = false;
      if (pollingState.current.timeout) {
        clearTimeout(pollingState.current.timeout);
        pollingState.current.timeout = null;
      }
    }
    /** @type {ChatMessage} */
    let newAIMessage = null;
    try {
      // robust unpacking of a message from the response
      newAIMessage = thread?.messages[thread.messages.length - 1];
      if (!newAIMessage) {
        console.error("No message from AI response", response);
        newAIMessage = {};
      }
      const json_content = get_json_content(thread);
      let content = json_content?.message || json_content?.answer;
      if (!content && json_content?.type === "canvas_edit") {
        // canvas_edit with no .message - e.g. truncated model output (report_writer has no
        // submit_answer finish-tool yet, ESG-3952 gap) got "repaired" into a valid-but-
        // incomplete dict missing .message. Never fall back to newAIMessage.content below,
        // which would be the raw canvas_edit JSON.
        content = "Applied an edit to the report.";
      }
      if (!content) {
        content = newAIMessage?.content;
      }
      if (!content) {
        if (response?.error) {
          content = response.rate_limit_error
            ? "Sorry - Service overloaded! Please try again later."
            : "Sorry - There was an error: " + response.error;
        } else if (response) {
          content = JSON.stringify(response);
        } else {
          content = "No response received from server.";
        }
      }
      if (typeof content !== "string") {
        // hack: multipart answer?
        content = multipartAnswerToString(content);
      }
      console.log("newAIMessage - change content", newAIMessage.content, content);
      newAIMessage.content = content;
    } catch (err) {
      console.error("Error processing AI response:", err);
      if (!response) {
        newAIMessage = {
          content: "Failed to get AI response.\nError: " + (error || err),
          role: "system",
          error: "" + (error || err),
        };
      } else {
        newAIMessage = { content: "Browser error processing AI response.\n" + err, role: "system", error: "" + err };
      }
      // send the error details to Sentry for potenttial debugging
      try {
        Sentry.captureException(error || err);
      } catch (sentryError) {
        console.error("(swallow) Error sending error to Sentry:", sentryError);
      }
    }
    // add the AI message / error to the messages
    console.log("newAIMessage", newAIMessage);
    const messagesWithAIMessage = [...messagesWithUserMessage, newAIMessage];
    setMessages(messagesWithAIMessage);
    scrollToBottom();
    // callback?
    if (onChatResponse) {
      onChatResponse(response);
    }
  },
    [isLoading]); // ./handleChatResponse

  /**
   * Poll the thread for updates.
   * Being called by
   *  1. handSendMessage to start polling if there is threadId estiablished.
   *  2. on page load and if the current thread is active (state is "running"). - to restore polling after a page reload.
   * @param {number} pollDelayMs - Delay in milliseconds between polls
   */
  const pollThread = useCallback(
    async (pollDelayMs) => {
      // ESG-3952: see chatQueueRouting.js's decidePollThreadAction() docstring for why this
      // guard exists - a queued (report-writer task) response never touches this threadId,
      // so acting on it while we haven't yet confirmed that risks firing on stale data.
      const action = decidePollThreadAction(pollingState.current);
      if (action === "wait_for_queue_check") {
        pollingState.current.timeout = setTimeout(() => pollThread(pollDelayMs), pollDelayMs);
        return;
      }
      if (action === "poll_thread" && threadId) {
        const threadData = await apiGetThread(threadId);
        assert(threadData.id === threadId);
        setThreadStatus(threadData.status);
        if (threadData.status_trace) setStatusTrace(threadData.status_trace);
        // has it changed??
        if (!threadWrapper.value || threadWrapper.value.version !== threadData.version) {
          console.log(
            "thread changed!",
            threadData.version,
            threadWrapper.value?.version,
            threadData.status,
            threadData
          );
          threadWrapper.value = threadData;
          if (onThreadUpdate) {
            onThreadUpdate(threadData);
          }
          if (threadData.messages?.length) {
            const lastMessage = threadData.messages[threadData.messages.length - 1];
            setLatestPolledMessage(lastMessage);
          }
        }
        // Stale thread detection: state is "running" but the backend process is dead
        if (pollingState.current.polling && threadData.state === "running" && threadData.is_active === false) {
          console.log("pollThread: thread is stale — backend process is no longer alive", threadData);
          pollingState.current.polling = false;
          if (pollingState.current.timeout) {
            clearTimeout(pollingState.current.timeout);
            pollingState.current.timeout = null;
          }
          setIsLoading(false);
          setIsStale(true);
          // Update messages from thread data so the user sees any partial response
          if (threadData.messages?.length) {
            setMessages(threadData.messages);
          }
          return;
        }
        // done? Check state enum (not status which is display text)
        if (pollingState.current.polling && (threadData.state === "done" || threadData.state === "failed")) {
          console.log("pollThread: state done! Handling response", threadData.state);
          // Stop polling and clear timeout
          pollingState.current.polling = false;

          if (pollingState.current.timeout) {
            clearTimeout(pollingState.current.timeout);
            pollingState.current.timeout = null;
          }

          // Handle the response
          let messagesWithUserMessage = threadData?.messages?.slice(0, -1) || []; // remove last AI message since it's added in handleChatResponse
          handleChatResponse(
            null,
            threadData,
            null,
            messagesWithUserMessage,
            setMessages,
            scrollToBottom,
            onChatResponse
          );

          setIsLoading(false);
        } else if (pollingState.current.polling) {
          // keep going - clear old timeout and set new one
          if (pollingState.current.timeout) {
            clearTimeout(pollingState.current.timeout);
          }
          pollingState.current.timeout = setTimeout(() => {
            pollThread(pollDelayMs);
          }, pollDelayMs);
        }
      }
    },
    [threadId, threadWrapper, onThreadUpdate, handleChatResponse, onChatResponse]
  );

  /**
   * Poll the report doc's `state` for a large-scope chat request that got routed to the
   * report-writer task pipeline instead of a normal synchronous chat response (ESG-3952 -
   * see esgai/app.py's POST /v0/chat routing and esgai/tasks/report_sections.py's
   * is_large_scope_chat_request()). Unlike pollThread(), there is no single thread_id to poll -
   * the task can run many section-worker threads in parallel - so this polls the reportdoc
   * itself, the same signal ReportDocEditorPage.jsx's GeneratingPanel already uses for the
   * initial-generation wizard step (reportDoc.state: PROCESSING -> DRAFT/ERROR).
   * @param {number} pollDelayMs
   */
  const pollReportDocTask = useCallback(
    async (pollDelayMs) => {
      if (!pollingState.current.polling || !reportdoc_id) return;
      let doc;
      try {
        doc = await reportDocService.get(reportdoc_id);
      } catch (err) {
        console.error("pollReportDocTask: error fetching reportDoc", err);
        // keep polling - a transient fetch error shouldn't give up on an in-flight task
        pollingState.current.timeout = setTimeout(() => pollReportDocTask(pollDelayMs), pollDelayMs);
        return;
      }
      if (doc.state === "PROCESSING") {
        if (pollingState.current.timeout) clearTimeout(pollingState.current.timeout);
        pollingState.current.timeout = setTimeout(() => pollReportDocTask(pollDelayMs), pollDelayMs);
        return;
      }
      // done (DRAFT) or errored (ERROR) - stop polling and surface a message, same shape as
      // handleChatResponse's normal completion path.
      pollingState.current.polling = false;
      if (pollingState.current.timeout) {
        clearTimeout(pollingState.current.timeout);
        pollingState.current.timeout = null;
      }
      setIsLoading(false);
      setActiveTaskId(null); // unmount the progress widget - the normal message list takes over
      const isError = doc.state === "ERROR";
      const content = isError
        ? "Sorry - there was an error generating the update. Please check the report and try again."
        : "Updated the report.";
      setMessages((prev) => [...prev, { role: "assistant", content }]);
      scrollToBottom();
      if (onThreadUpdate) onThreadUpdate(doc);
      if (onChatResponse) onChatResponse(doc);
    },
    [reportdoc_id, onThreadUpdate, onChatResponse]
  );

  // for restarting a thread with chat history, load the messages
  // note: not using useQuery because user input gets added instantly to messages - hence useState + useEffect
  useEffect(() => {
    if (threadId && messages.length === 0) {
      setLoadingThread(true);
      apiGetThread(threadId)
        .then((data) => {
          threadWrapper.value = data;
          setMessages(data.messages);
          // if the thread is active (state is "running") then poll for updates
          if (!data.state || data.state === "running") {
            setIsLoading(true);
            pollingState.current.polling = true;
            pollingState.current.timeout = setTimeout(() => pollThread(1000), 1000);
          } else {
            console.log("thread is not active", data.state, data.status, data);
          }
        })
        .finally(() => {
          setLoadingThread(false);
        });
    }
  }, [threadId, threadWrapper, pollThread, messages]);

  // Cleanup polling timeout on unmount
  useEffect(() => {
    const state = pollingState.current;
    return () => {
      if (state.timeout) {
        clearTimeout(state.timeout);
        state.timeout = null;
      }
      state.polling = false;
      state.awaitingQueueCheck = false;
    };
  }, []);

  let aiContext = getAIContext();

  useEffect(() => {
    // Use contextual prompts from the rules system.
    // Filter by chatShowWhen: "initial" (fresh only), "in-conversation" (with history), "both" or null (always).
    if (contextualPrompts?.length) {
      // A seeded intro message (message.isIntro - e.g. the report editor's "your report is
      // ready" greeting) is NOT conversation history: it's the opening state of a fresh chat.
      // Counting it as history would hide every chatShowWhen: "initial" prompt on exactly the
      // screens the intro exists for.
      const hasHistory = messages?.some((message) => !message.isIntro) || false;
      const applicable = contextualPrompts.filter((p) => {
        const chatShowWhen = p.rules?.chatShowWhen || "both";
        if (hasHistory) return chatShowWhen === "in-conversation" || chatShowWhen === "both";
        return chatShowWhen === "initial" || chatShowWhen === "both";
      });
      setButtons(applicable.map((p) => ({ label: p.description || p.name, message: p.content, displayMessage: p.description || p.name })));
    } else {
      setButtons([]);
    }
    scrollToBottom();
  }, [messages, messages?.length, contextualPrompts]);

  // look for a Q-Round and use that folder to limit the sources
  // Use questionnaireId from AI context if available (e.g. foundation round not in Redux)
  const roundSelector = useSelector((state) => state.issbRound);
  const roundId = aiContext?.questionnaireId || roundSelector?.currentRoundId;
  const folder = getFolderForRound(roundId);
  const { getAccessTokenSilently } = useAuth();
  // Note: avoid using cached round data here -- as sources may be stale
  const { data: currentRound } = useQuery(
    roundId,
    async () => {
      await getAccessTokenSilently();
      return questionnaireService.get(roundId);
    },
    {
      enabled: !!roundId,
    }
  );
  const sourceRoundIds = currentRound?.sourceRoundIds;

  /*
    The round's approved Foundation Data v2 figures, as AI Review drafts from them (ESG-4020). Without a year in
    the request the AI service fetches none, so the assistant could neither see nor cite the figures an answer
    was drafted from. Always on where there is a round; the report writer is excluded, as it has no round here.
    Which entities' figures is decided server-side from `active_round_ids`.
  */
  const foundationEnabled = !!roundId && agent !== AGENT_CANVAS && isModuleAvailable(MODULES.FoundationData.id);
  const { data: foundationYears } = useFoundationV2Years({ enabled: foundationEnabled });
  const foundationYearIds = chatFoundationYearIds(foundationYears, currentRound, { enabled: foundationEnabled });

  // Report writer / fixed FileInfoSpec: scope is rounds+questions (SourcesAndFilters), not round folder files
  const loadRoundFolderFiles = !fileinfoSpec && !cannotAddSource;

  // Load source files for the current round's folder (answer chat only)
  const { data: roundSourceFiles } = useQuery(
    ["roundSourceFiles", folder],
    async () => {
      await getAccessTokenSilently();
      const page = await fileinfoService.list({ size: 1000 });
      const filtered = (page?.content || []).filter((f) => folder && f.folders?.includes(folder));
      return normalizeFileInfoList(filtered);
    },
    { enabled: !!folder && loadRoundFolderFiles }
  );

  const handleSendMessage = async (directText, displayText) => {
    const messageText = (typeof directText === "string" ? directText : "") || inputValue;
    if (!messageText.trim()) return;
    // For prompt-driven sends, the bubble shows `displayText` while the LLM receives `messageText`.
    const displayMessage = typeof displayText === "string" && displayText ? displayText : undefined;
    // add the user message to the messages
    setIsLoading(true);
    setIsStale(false);
    setMessageSentAt(new Date());
    // Reset any stale progress widget from a PRIOR queued exchange - this new message might
    // be a small edit that never re-sets activeTaskId, and we don't want the old task's
    // (already-finished) progress bar lingering under the new "I'm thinking" state.
    setActiveTaskId(null);
    const userMessage = displayMessage
      ? { content: messageText, display_content: displayMessage, role: "user" }
      : { content: messageText, role: "user" };
    const messagesWithUserMessage = [...messages, userMessage];
    setMessages(messagesWithUserMessage);
    setInputValue("");
    scrollToBottom();
    // call the AI...
    /** @type {ChatResponse} */
    let response = null;
    let error = null;
    let thread = null;
    try {
      const questionAttachedIds = preferIdsFromQuestionFiles(aiContext?.files);
      const userPreferIds = normalizeFileinfoIdList(fileinfos?.map((f) => f.id)) || [];
      let _fileinfoSpec = normalizeFileInfoSpec(fileinfoSpec ? { ...fileinfoSpec } : {});
      if (!fileinfoSpec) {
        _fileinfoSpec = normalizeFileInfoSpec({
          ..._fileinfoSpec,
          prefer_fileinfo_ids: normalizeFileinfoIdList([...userPreferIds, ...questionAttachedIds]),
          exclude_fileinfo_ids: normalizeFileinfoIdList(excludedList?.map((f) => f.id)),
          folder_id: folder,
          round_ids: sourceRoundIds,
        });
      } else if (questionAttachedIds.length) {
        _fileinfoSpec = normalizeFileInfoSpec({
          ..._fileinfoSpec,
          prefer_fileinfo_ids: normalizeFileinfoIdList([
            ...(_fileinfoSpec.prefer_fileinfo_ids || []),
            ...userPreferIds,
            ...questionAttachedIds,
          ]),
        });
      }
      // Both branches: a fixed spec from the caller is about documents and rounds, and says nothing about
      // foundation data. Left unset rather than [] when there is none, so the request reads as it did before.
      if (foundationYearIds.length && !_fileinfoSpec.foundation_year_ids?.length) {
        _fileinfoSpec = { ..._fileinfoSpec, foundation_year_ids: foundationYearIds };
      }
      // HACK: the report-writer can't see the current round - so don't send the ID
      const active_round_ids = agent === AGENT_CANVAS ? null : [roundId];

      // Call the AI!
      setThreadStatus("Calling the AI service");

      // there is threadId? just post, abandon (async) and start polling.
      if (threadId) {
        console.log("handleSendMessage: have threadId - postChat and start polling");
        // ESG-3952: block pollThread() from acting on this (possibly stale, from a PRIOR
        // exchange) threadId until we know whether THIS request got queued as a
        // report-writer task - a queued response never touches this thread_id at all. See
        // pollThread()'s awaitingQueueCheck guard.
        pollingState.current.awaitingQueueCheck = true;
        postChat({
          threadId,
          message: messageText,
          displayMessage,
          fileinfoSpec: _fileinfoSpec,
          agent,
          speed: slow ? "LOW" : "MEDIUM",
          active_round_ids,
          reportdoc_id,
          saveEdits: !!reportdoc_id,
        }).then((resp) => {
          pollingState.current.awaitingQueueCheck = false;
          if (isQueuedTaskResponse(resp)) {
            // Large-scope canvas request routed to the report-writer task pipeline instead
            // of a normal chat response - poll the reportdoc's state instead of this thread.
            console.log("handleSendMessage: request was queued as a task - switching to reportdoc polling", resp);
            pollingState.current.polling = true;
            if (pollingState.current.timeout) clearTimeout(pollingState.current.timeout);
            pollingState.current.timeout = setTimeout(() => pollReportDocTask(2000), 2000);
            setActiveTaskId(resp.task_id); // show real progress instead of the stale spinner text
          }
          // else: normal chat response for this thread - the pollThread() timeout already
          // set below picks it up on its next tick (awaitingQueueCheck is now false).
        }).catch((err) => {
          pollingState.current.awaitingQueueCheck = false;
          console.error("handleSendMessage: error checking postChat response for queued task", err);
        });
        pollingState.current.polling = true;
        pollingState.current.timeout = setTimeout(() => pollThread(1000), 1000);
      } else {
        // no threadId yet - need to wait for postChat to return threadId. Usually at the start of the conversation.
        console.log("handleSendMessage: no threadId - postChat and wait for response");
        response = await postChat({
          threadId,
          message: messageText,
          displayMessage,
          fileinfoSpec: _fileinfoSpec,
          agent,
          speed: slow ? "LOW" : "MEDIUM",
          active_round_ids,
          reportdoc_id,
          saveEdits: !!reportdoc_id,
        });

        if (isQueuedTaskResponse(response)) {
          // ESG-3952: large-scope canvas request routed to the report-writer task pipeline -
          // poll the reportdoc's state (task can run many section-worker threads in
          // parallel, so there's no single thread_id to poll - see pollReportDocTask()).
          console.log("handleSendMessage: request was queued as a task - polling reportdoc", response);
          pollingState.current.polling = true;
          pollingState.current.timeout = setTimeout(() => pollReportDocTask(2000), 2000);
          setActiveTaskId(response.task_id); // show real progress instead of the stale spinner text
          return;
        }

        thread = response.thread;
        const newThreadId = response.thread?.id;
        if (!threadId && newThreadId) {
          setThreadId(newThreadId);
          // Start polling if we got a new threadId and thread state is "running"
          if (thread?.state === "running") {
            pollingState.current.polling = true;
            if (!pollingState.current.timeout) {
              pollingState.current.timeout = setTimeout(() => pollThread(1000), 1000);
            }
          }
        }
        handleChatResponse(
          response,
          thread,
          error,
          messagesWithUserMessage,
          setMessages,
          scrollToBottom,
          onChatResponse
        );
      }
    } catch (err) {
      error = err;
      console.error("Error fetching AI response:", error);
      handleChatResponse(response, thread, error, messagesWithUserMessage, setMessages, scrollToBottom, onChatResponse);
    }
  };

  // fileinfos = user-explicitly-added files (sent as prefer_fileinfo_ids to the AI)
  // roundSourceFiles = files already in the round folder (available via folder_id/round_ids)
  // displaySourceFiles = combined list shown in the SourcesPanel UI
  const [fileinfos, setFileinfos] = useState([]);
  useEffect(() => {
    const ids = preferIdsFromQuestionFiles(aiContext?.files);
    if (!ids.length) return;
    const raw = aiContext.files;
    if (typeof raw[0] === "object" && raw[0]?.id != null) {
      setFileinfos(normalizeFileInfoList(raw));
    } else {
      setFileinfos(ids.map((id) => ({ id })));
    }
  }, [aiContext]);

  const displaySourceFiles = React.useMemo(() => {
    const userFiles = normalizeFileInfoList(fileinfos);
    if (!loadRoundFolderFiles) return userFiles;
    const roundFiles = normalizeFileInfoList(roundSourceFiles || []);
    return [
      ...roundFiles,
      ...userFiles.filter((f) => !roundFiles.some((rf) => fileinfoIdEquals(rf.id, f.id))),
    ];
  }, [loadRoundFolderFiles, roundSourceFiles, fileinfos]);

  const [excludedList, setExcludedList] = useState([]);
  const handleFileInfoListChange = (fileInfoList) => {
    console.log("handleFileInfoListChange", fileInfoList);
    // Only track user-added files (not round source files) for the fileinfoSpec
    const roundFiles = loadRoundFolderFiles ? normalizeFileInfoList(roundSourceFiles || []) : [];
    const userAdded = normalizeFileInfoList(
      fileInfoList.filter((f) => !roundFiles.some((rf) => fileinfoIdEquals(rf.id, f.id)))
    );
    // Send new files for AI analysis
    for (let fileInfo of userAdded) {
      if (fileInfo.aiDate || fileInfo._aiAnalysisRequested) continue;
      console.log("send to AI", fileInfo);
      postSourcesUpdate([fileInfo.id], true).then((data) =>
        console.log("ai analysis result", data, "for file", fileInfo)
      );
      fileInfo._aiAnalysisRequested = true; // avoid multiple requests
    }
    aiContext.files = userAdded;
    setFileinfos(userAdded);
  };
  const handleExcludedListChange = (list) => {
    const normalized = normalizeFileInfoList(list);
    aiContext.excludedFiles = normalized;
    setExcludedList(normalized);
  };
  const handleToggleFile = (file) => {
    const normalized = normalizeFileInfo(file);
    const isExcluded = excludedList.some((f) => fileinfoIdEquals(f.id, normalized.id));
    if (isExcluded) {
      handleExcludedListChange(excludedList.filter((f) => !fileinfoIdEquals(f.id, normalized.id)));
    } else {
      handleExcludedListChange([...excludedList, normalized]);
    }
  };

  // Keep ref updated so external events can call handleSendMessage
  handleSendRef.current = handleSendMessage;

  // don't show tool results
  let displayMessages = messages;
  if (!showAllMessages) {
    displayMessages = displayMessages.filter(
      (message) =>
        message.role !== "tool" && message.role !== "tool_result" && message.role !== "critic" && message.content
    );
    // or tool results
    displayMessages = displayMessages.filter((message) => message.json_content?.type !== "function");
    // filter out archived messages
    displayMessages = displayMessages.filter((message) => message.state !== LifecycleState.ARCHIVED);
  }

  if (loadingThread) {
    return (
      <Box>
        <LocalSpinner mt={4} w="100px" h="100px" />
        <Text>Loading thread...</Text>
      </Box>
    );
  }
  const isEmpty = !displayMessages.length && dontShowEmptyChatDefaultMessage === false;

  const chatInputBox = (
    <Box
      border="2px solid"
      borderColor="gray.200"
      borderRadius="2xl"
      bg="white"
      _focusWithin={{ borderColor: "headingLime" }}
      transition="border-color 0.2s"
      position="relative"
      mb={1}
    >
      <Textarea
        className="chat-text-input"
        variant="unstyled"
        placeholder="Type a message..."
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        fontSize="body.sm"
        px={4}
        pt={3}
        pb={1}
        minH="80px"
        maxH="160px"
        resize="none"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
      />
      {/* Bottom toolbar */}
      <HStack px={3} pb={2} justify="space-between">
        <HStack spacing={1}>
          <SourcesButton
            fileinfos={displaySourceFiles}
            excludedList={excludedList}
            selectedCount={displaySourceFiles.length - excludedList.length}
            onToggleFile={handleToggleFile}
            onAddFiles={handleFileInfoListChange}
            cannotAddSource={cannotAddSource}
            folder={folder}
          />
        </HStack>
        <HStack spacing={1}>
          <DevOnly>
            <Button
              title="Toggle slow AI mode"
              size="xs"
              variant={slow ? "primary" : "ghost"}
              onClick={(e) => setSlow(!slow)}
              p={1}
              minW="auto"
            >
              <HourglassMedium size={16} />
            </Button>
            <Button size="xs" variant="ghost" onClick={(e) => setShowAllMessages(!showAllMessages)} p={1} minW="auto">
              {showAllMessages ? <Eye size={16} /> : <EyeClosed size={16} />}
            </Button>
            {onToggleHistory && (
              <Button size="xs" variant="ghost" onClick={onToggleHistory} p={1} minW="auto">
                <ClockCounterClockwise size={16} />
              </Button>
            )}
          </DevOnly>
          <Button
            variant="ghost"
            onClick={handleSendMessage}
            className="chat-text-send-button"
            p={1}
            minW="auto"
            h="auto"
            borderRadius="full"
            _hover={{ bg: "gray.100" }}
          >
            <ArrowCircleUp size={28} weight="fill" color={inputValue.trim() ? colors.darkTeal : "#CBD5E0"} />
          </Button>
        </HStack>
      </HStack>
    </Box>
  );

  return (
    <Box height={height || "100%"} display="flex" flexDirection="column" overflow="hidden">
      <Flex
        flex={1}
        minH={0}
        overflowY="auto"
        flexDirection="column"
        pr={1}
        sx={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.15) transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.15)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0,0,0,0.3)",
          },
        }}
      >
        {displayMessages.length > 0 &&
          displayMessages.map((message, index) => (
            <AIChatMessage
              key={index}
              threadId={threadId}
              message={message}
              setMessages={setMessages}
              allMessages={messages}
              isLastMessage={index === messages.length - 1}
              debug={showAllMessages}
              onSendMessage={handleSendMessage}
            />
          ))}
        {isEmpty && (
          <NoMessages setMessage={setInputValue} onSend={handleSendMessage} buttons={buttons} inputArea={chatInputBox} />
        )}
        {isLoading && activeTaskId && (
          // ESG-3952: a large-scope canvas request was routed to a report-writer task (see
          // pollReportDocTask/isQueuedTaskResponse) - show REAL, live-updating progress
          // (task.progress via GET /v0/task/{id}) instead of a stale/generic spinner. This can
          // run 30s-2min+ (planner call + parallel section workers + combine), so a plain
          // spinner with no movement reads as "stuck", not "working".
          <Box display="flex" flexDirection="row" alignItems="center">
            <AIProgressWidget taskId={activeTaskId} refetchInterval={2000} />
            <Box style={{ minHeight: "40px", marginLeft: "5px" }}>
              <Text>Updating your report...</Text>
            </Box>
          </Box>
        )}
        {isLoading && !activeTaskId && (
          /* Note: this Box wrapper is needed for correct scrolling behaviour */ <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <LocalSpinner mt={4} w="100px" h="100px" />
            <Box style={{ minHeight: "40px", marginLeft: "5px" }}>
              <Text>I'm thinking</Text>
              <Thinking threadStatus={threadStatus} statusTrace={statusTrace} latestPolledMessage={latestPolledMessage} messageSentAt={messageSentAt} />
            </Box>
          </Box>
        )}
        {isStale && (
          <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3} mt={2}>
            <Text fontSize="sm" color="orange.800" mb={2}>AI stopped responding. This may be due to a server restart or timeout.</Text>
            <Button size="sm" colorScheme="orange" variant="outline" onClick={() => {
              const lastUserMsg = [...messages].reverse().find(m => m.role === "user");
              if (lastUserMsg) handleSendMessage(lastUserMsg.content);
            }}>Retry</Button>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Flex>

      {/* chat input — hidden when empty state is shown, as it's rendered inside NoMessages */}
      {!isEmpty && (
        <Box flexShrink={0}>
          <Divider mt={4} mb={2} />
          {!isLoading && messages.length > 0 && buttons.length > 0 && (
            <Flex gap={2} flexWrap="wrap" mb={2}>
              {buttons.map((btn) => {
                const label = typeof btn === "string" ? btn : btn.label;
                const message = typeof btn === "string" ? btn : btn.message;
                const displayMessage = typeof btn === "string" ? undefined : btn.displayMessage;
                return (
                  <Button
                    key={label}
                    variant="outline"
                    size="sm"
                    borderRadius="full"
                    borderColor="gray.300"
                    color="gray.600"
                    fontWeight="normal"
                    bg="white"
                    _hover={{ borderColor: "headingLime", color: "darkTeal" }}
                    onClick={() => handleSendMessage(message, displayMessage)}
                  >
                    {label}
                  </Button>
                );
              })}
            </Flex>
          )}
          {chatInputBox}
        </Box>
      )}
    </Box>
  );
}

/**
 * Dev-only: This is NOT ready for users yet!
 * Note: this isn't true streaming - it doesnt get answer-in-progress tokens from the AI.
 * What it gets is updates as the agent loop moves through the steps.
 * Show interim feedback from the AI
 */
function Thinking({ threadStatus, statusTrace, latestPolledMessage, messageSentAt }) {
  let message = displayThreadStatus(threadStatus);
  const messageType = latestPolledMessage?.json_content?.type;
  if (messageType === "function") {
    message = "Tool call: " + latestPolledMessage.json_content.function?.name;
  }
  // Only show traces from after the user sent their message (no messageSentAt = page refresh, skip all historical traces)
  const recentTrace = messageSentAt ? (statusTrace?.filter(entry => new Date(entry.at) >= messageSentAt) || []) : [];
  return (
    <Box>
      {recentTrace.length > 0 && (
        <VStack align="start" spacing={0} mb={1}>
          {recentTrace.map((entry, i) => (
            <Text key={i} fontSize="xs" color="gray.400">
              {new Date(entry.at).toLocaleTimeString()} — {entry.status}
            </Text>
          ))}
        </VStack>
      )}
      <SmallText>{message}</SmallText>
    </Box>
  );
}

function NoMessages({ setMessage, onSend, buttons, inputArea }) {
  return (
    <Flex direction="column" flex={1} minH={0}>
      {/* Tagline at top */}
      <VStack pt={4} pb={4}>
        <Sparkle size={64} weight="fill" color={colors.headingLime} />
        <Text textAlign="center" fontSize="heading.m" color="teal" variant="subtitle">
          Guiding you every step of the way for seamless reporting.
        </Text>
      </VStack>
      {/* Buttons + input centered in remaining space */}
      <Flex direction="column" flex={1} justify="center" align="center" px={2}>
        <Flex gap={2} flexWrap="wrap" justify="center" mb={4}>
          {buttons.map((btn) => {
            const label = typeof btn === "string" ? btn : btn.label;
            const message = typeof btn === "string" ? btn : btn.message;
            const displayMessage = typeof btn === "string" ? undefined : btn.displayMessage;
            return (
              <Button
                key={label}
                variant="outline"
                size="sm"
                borderRadius="full"
                borderColor="gray.300"
                color="gray.600"
                fontWeight="normal"
                bg="white"
                _hover={{ borderColor: "headingLime", color: "darkTeal" }}
                onClick={() => onSend ? onSend(message, displayMessage) : setMessage(message)}
              >
                {label}
              </Button>
            );
          })}
        </Flex>
        <Box w="100%">{inputArea}</Box>
      </Flex>
    </Flex>
  );
}

function SourcesButton({ fileinfos, excludedList, selectedCount, onToggleFile, onAddFiles, cannotAddSource, folder }) {
  const toast = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAddSource, setShowAddSource] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const panelRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsExpanded(false);
        setShowAddSource(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded]);

  const handleOpenAddSource = useCallback(async () => {
    setShowAddSource(true);
    setSearchText("");
    setSearchLoading(true);
    try {
      const page = await fileinfoService.list({ size: 1000 });
      const allAvailable = normalizeFileInfoList(page?.content || []).filter(
        (f) => !fileinfos.some((existing) => fileinfoIdEquals(existing.id, f.id))
      );
      setSearchResults(allAvailable);
    } catch (e) {
      console.warn("Failed to load files for add source", e);
      setSearchResults([]);
    }
    setSearchLoading(false);
  }, [fileinfos]);

  const handleAddFile = useCallback(
    (file) => {
      const normalized = normalizeFileInfo(file);
      onAddFiles(normalizeFileInfoList([...fileinfos, normalized]));
      setSearchResults((prev) => prev.filter((f) => !fileinfoIdEquals(f.id, normalized.id)));
    },
    [fileinfos, onAddFiles]
  );

  const handleFileUpload = useCallback(
    async (fileList) => {
      if (!fileList?.length) return;
      setUploading(true);
      const uploaded = [];
      const failed = [];
      for (const file of Array.from(fileList)) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          if (folder) formData.append("folders", [folder]);
          const newFileInfo = await apiUpload({ data: formData });
          uploaded.push(newFileInfo);
        } catch (err) {
          console.error("Error uploading file", err);
          failed.push(file.name);
        }
      }
      if (uploaded.length > 0) {
        onAddFiles(normalizeFileInfoList([...fileinfos, ...uploaded]));
      }
      if (failed.length > 0) {
        showErrorToast(toast, `Failed to upload ${failed.length} file(s): ${failed.join(", ")}`);
      }
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    },
    [folder, fileinfos, onAddFiles, toast]
  );

  const filteredResults = searchText.trim()
    ? searchResults.filter((f) => {
        const name = (f.title || f.filename || "").toLowerCase();
        return name.includes(searchText.toLowerCase());
      })
    : searchResults;

  if (cannotAddSource && (!fileinfos || fileinfos.length === 0)) return null;

  return (
    <Box position="relative" ref={panelRef}>
      {/* Compact trigger button */}
      <HStack
        as="button"
        spacing={1.5}
        px={3}
        py={1}
        h="32px"
        border="1px solid"
        borderColor={isExpanded ? "headingLime" : "gray.200"}
        borderRadius="full"
        cursor="pointer"
        color={isExpanded ? "darkTeal" : "gray.500"}
        bg={isExpanded ? "gray.50" : "white"}
        _hover={{ borderColor: "headingLime", color: "darkTeal" }}
        transition="all 0.15s"
        onClick={() => {
          setIsExpanded(!isExpanded);
          if (isExpanded) setShowAddSource(false);
        }}
      >
        <Files size={14} />
        <Text fontSize="body.xs" fontWeight="medium">Sources</Text>
        {fileinfos.length > 0 && (
          <Box bg={colors.limeTint[100]} borderRadius="full" px={1.5} py={0}>
            <Text fontSize="10px" color={colors.headingLime} fontWeight="semibold">{selectedCount}</Text>
          </Box>
        )}
        {isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </HStack>

      {/* Dropdown panel */}
      {isExpanded && (
        <Box
          position="absolute"
          bottom="100%"
          left={0}
          mb={2}
          w="320px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          bg="white"
          boxShadow="lg"
          overflow="hidden"
          zIndex={10}
        >
          <VStack spacing={0} align="stretch" maxH="200px" overflowY="auto">
            {fileinfos.map((file, i) => {
              const isExcluded = excludedList.some((f) => fileinfoIdEquals(f.id, file.id));
              const name = file.title || file.filename || file.id;
              return (
                <HStack
                  key={file.id || i}
                  py={2}
                  px={3}
                  spacing={3}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => onToggleFile(file)}
                >
                  <Checkbox
                    isChecked={!isExcluded}
                    colorScheme="green"
                    size="sm"
                    pointerEvents="none"
                  />
                  <Text
                    fontSize="body.xs"
                    color={isExcluded ? "gray.400" : "gray.700"}
                    noOfLines={1}
                    flex={1}
                  >
                    {name}
                  </Text>
                </HStack>
              );
            })}
          </VStack>

          {/* Add new source link */}
          {!cannotAddSource && !showAddSource && (
            <HStack
              px={3}
              py={2}
              spacing={2}
              cursor="pointer"
              color={colors.headingLime}
              _hover={{ color: "darkTeal" }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenAddSource();
              }}
            >
              <Plus size={16} weight="bold" />
              <Text fontSize="body.xs" fontWeight="medium">
                Add new source
              </Text>
            </HStack>
          )}

          {/* Search / upload panel */}
          {showAddSource && (
            <Box px={3} py={2} borderTop="1px solid" borderColor="gray.100">
              <HStack mb={2}>
                <HStack flex={1} bg="gray.50" borderRadius="md" px={2} py={1}>
                  <MagnifyingGlass size={14} color="gray" />
                  <Input
                    placeholder="Search sources..."
                    size="sm"
                    variant="unstyled"
                    fontSize="body.xs"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                </HStack>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddSource(false);
                  }}
                >
                  <X size={14} />
                </Button>
              </HStack>
              <VStack spacing={0} align="stretch" maxH="120px" overflowY="auto">
                {searchLoading && (
                  <Text fontSize="body.xxs" color="gray.400" p={2}>
                    Loading...
                  </Text>
                )}
                {!searchLoading && filteredResults.length === 0 && (
                  <Text fontSize="body.xxs" color="gray.400" p={2}>
                    No additional sources found
                  </Text>
                )}
                {!searchLoading &&
                  filteredResults.map((file) => {
                    const name = file.title || file.filename || file.id;
                    return (
                      <HStack
                        key={file.id}
                        py={1.5}
                        px={1}
                        spacing={2}
                        borderBottom="1px solid"
                        borderColor="gray.50"
                        cursor="pointer"
                        _hover={{ bg: "gray.50" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddFile(file);
                        }}
                      >
                        <Text fontSize="body.xs" color="darkTeal" noOfLines={1} flex={1} title={name}>
                          {name}
                        </Text>
                        <Plus size={14} color="gray" />
                      </HStack>
                    );
                  })}
              </VStack>
              <HStack pt={2} spacing={2}>
                <Button
                  variant="outline"
                  size="sm"
                  borderRadius="full"
                  borderColor="gray.300"
                  color="gray.600"
                  fontWeight="normal"
                  leftIcon={uploading ? <Spinner size="xs" /> : <UploadSimple size={14} />}
                  bg="white"
                  _hover={{ borderColor: "headingLime", color: "darkTeal" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  isDisabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload file"}
                </Button>
              </HStack>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => handleFileUpload(e.target.files)}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
